import { refreshCurrentLegacyFrame, useCurrentAgreement } from '@economic/react-app';
import { Alert, Button, Dialog, Group, Icon, ToastReference, useToast } from '@economic/taco';
import { t, Trans } from '@lingui/macro';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LoadingBox from '~/app/pages/home/PlanUpgradeDialog/LoadingBox';
import ExternalLink from '~/app/shared/components/ExternalLink';
import { Form } from '~/app/shared/components/Form';
import { useLocalization } from '~/app/shared/components/l10n/l10n';
import { getErrorMessage } from '~/utilities/parseAPIValidationErrors';
import internalApiClient from '~/app/shared/util/api/internalApiClient';
import { SkatVatIntegrationOnboardingInitiationModel } from '~/types/internal-api';

export function ConnectToSkatVatDialog() {
    const location = useLocation();
    const navigate = useNavigate();
    const agreement = useCurrentAgreement();
    const textsLoading = useLocalization(textIds);

    const [errorMessage, setErrorMessage] = useState();
    const [checkForConnection, setCheckForConnection] = useState<boolean>(false);

    const toast = useToast();
    let activeToast: ToastReference;

    function closeDialog() {
        navigate(location.pathname);
    }

    function refreshAndClose() {
        refreshCurrentLegacyFrame('content');
        closeDialog();
    }

    if (location.hash !== '#connect-to-skat-vat' || !agreement.features.skatVatIntegration) {
        return null;
    }

    async function initiateOnboarding() {
        activeToast = toast.loading(<></>);
        const response = await internalApiClient.post<SkatVatIntegrationOnboardingInitiationModel>(
            'reports/vat/skat-integration/onboarding'
        );
        activeToast.close();

        window.open(response.data.skatOnboardingUrl);
        setCheckForConnection(true);
    }

    function handleInitiationError(error) {
        const message = getErrorMessage(error);
        setErrorMessage(message);
    }

    if (textsLoading) {
        return (
            <Dialog size="sm" defaultOpen onClose={closeDialog}>
                <Dialog.Content aria-label={t`Give e-conomic permission to transfer your VAT statement`}>
                    <LoadingBox />
                </Dialog.Content>
            </Dialog>
        );
    }

    return (
        <Dialog size="sm" defaultOpen onClose={closeDialog}>
            <Dialog.Content aria-label={t`Give e-conomic permission to transfer your VAT statement`}>
                {({ close }) => (
                    <Form skipNotifications onSubmit={initiateOnboarding} onSubmitError={handleInitiationError}>
                        <Dialog.Title>
                            <Trans>Give e-conomic permission to transfer your VAT statement</Trans>
                        </Dialog.Title>
                        <div className="pt-4">
                            <h5 className="mb-4">
                                <Trans>
                                    By automatically transferring your VAT statement, it becomes much easier to report VAT. You
                                    just have to remember to approve your figures at Skat.dk.
                                </Trans>
                            </h5>
                            <p className="mb-4">
                                <span>
                                    <Trans>
                                        In order to connect to Skat.dk, e-conomic needs to be authorised as your accounting
                                        service provider. Follow these few steps to establish a connection:
                                    </Trans>
                                </span>
                            </p>
                            <ol className="mb-4">
                                <li>
                                    <Trans>1. Click on “Connect to SKAT”</Trans>
                                </li>
                                <li>
                                    <Trans>
                                        2. You will now be forwarded to TastSelv Erhverv at Skat.dk, where you must log in with
                                        the company's MitID
                                    </Trans>
                                </li>
                                <li>
                                    <Trans>3. Select Profile and contact information</Trans>
                                </li>
                                <li>
                                    <Trans>4. Select Rights and access to TastSelv</Trans>
                                </li>
                                <li>
                                    <Trans>5. Select Authorise auditors and others</Trans>
                                </li>
                                <li>
                                    <Trans>6. Select “Nemvirksomhed – adgang for Regnskabssystemsudbyder”</Trans>
                                </li>
                                <li>
                                    <Trans>7. Insert e-conomic’s CVR no.: 29403473</Trans>
                                </li>
                                <li>
                                    <Trans>8. Press “Save”</Trans>
                                </li>
                            </ol>
                            <p className="mb-4">
                                <Trans>
                                    Read more about how to establish the connection to SKAT on{' '}
                                    <ExternalLink href="https://www.e-conomic.dk/support/artikler/opsaetning-af-momsoverfoersel-til-skat">
                                        e-copedia
                                    </ExternalLink>
                                    .
                                </Trans>
                            </p>
                        </div>
                        {checkForConnection && (
                            <Alert state="information">
                                <Trans>
                                    If you have authorized e-conomic as your accounting provider, reload the page to see if the
                                    connection is established and active.
                                </Trans>
                            </Alert>
                        )}
                        {errorMessage && <Alert state="error">{errorMessage}</Alert>}
                        <Dialog.Footer>
                            <Group>
                                <Button appearance="default" onClick={close}>
                                    <Trans>Cancel</Trans>
                                </Button>
                                {checkForConnection ? (
                                    <Button appearance="primary" onClick={refreshAndClose}>
                                        <Trans>Refresh</Trans>
                                    </Button>
                                ) : (
                                    <Button appearance="primary" type="submit">
                                        <span>
                                            <Trans>Connect to SKAT</Trans>
                                        </span>
                                        <Icon name="undock" />
                                    </Button>
                                )}
                            </Group>
                        </Dialog.Footer>
                    </Form>
                )}
            </Dialog.Content>
        </Dialog>
    );
}

const textIds = [
    'ERROR_CODE_SkatVatIntegrationOnboardingMissingCvr',
    'ERROR_CODE_SkatVatIntegrationOnboardingInvalidCvr',
    'ERROR_CODE_SkatVatIntegrationOnboardingCvrCannotBeUsed',
    'ERROR_CODE_SkatVatIntegrationOnboardingAlreadyOnboarded',
];
